import { DatesSetArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import AddIcon from '@mui/icons-material/Add';
import { Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import {
  InterviewBlock,
  UserEvent,
  EventsByMonth,
} from '@features/enterpriseEvents';
import InterviewBlockModal from '@features/enterpriseEvents/components/AddInterviewBlockModal/InterviewBlockModal';
import EventInfoModal from '@features/enterpriseEvents/components/EventInfoModal/EventInfoModal';
import UpcomingEvents from '@features/enterpriseEvents/components/UpcomingEvents/UpcomingEvents';
import { Application } from '@features/applications';
import { useGetEnterpriseEventsInterview } from '@features/enterpriseEvents/hooks/useGetEnterpriseEventsInterview';
import CompanyDropdownModal from '@features/enterpriseEvents/components/CompanyDropdownModal/CompanyDropdownModal';
import { useGetEnterpriseEvents } from '@features/enterpriseEvents/hooks/useGetEnterpriseEvents';
import LocationSearch from '@features/applications/components/LocationSearch/LocationSearch';
import { getEventColor } from '@utils/Interviews';
import { white } from '@styles/colors';

type CompanyDetail = {
  name: string;
  company_id: number;
} | null;

export const EnterpriseCalender = () => {
  const [isAddInterviewBlockModalOpen, setIsInterviewBlockModalOpen] =
    useState(false);
  const [isComapnyModalOpen, setIsComapnyModalOpen] = useState(false);
  const [companyDetail, setCompanyDetail] = useState<CompanyDetail>(null);
  const [selectedEvent, setSelectedEvent] = useState<
    InterviewBlock | UserEvent | null
  >(null);
  const [events, setEvents] = useState<EventsByMonth | []>([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [prevMonth, setPrevMonth] = useState(dayjs().subtract(1, 'month'));
  const [nextMonth, setNextMonth] = useState(dayjs().add(1, 'month'));
  const [pageNo, setPageNo] = useState(1);
  const [upcomingInterviews, setUpcomingInterviews] = useState<
    EventsByMonth | []
  >([]);
  const [page, setPage] = React.useState(1);
  const [selectedBlock, setSelectedBlock] = React.useState(null);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [singleBlockInterview, setSingleBlockInterview] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number>(
    '',
  );
  const [dateInfo, setDateInfo] = useState<{ month: string; year: string }>({
    month: dayjs().format('MM'),
    year: dayjs().format('YYYY'),
  });

  const { isLoadingBlocks } = useGetEnterpriseEvents({
    setEvents,
    selectedCompanyId,
    dateInfo,
  });

  const { isUpcommingEventsLoading } = useGetEnterpriseEventsInterview({
    setUpcomingInterviews,
    selectedCompanyId,
    dateInfo,
  });

  const handleDateChange = (rangeInfo: DatesSetArg) => {
    const startDate = dayjs(rangeInfo.end);
    const middleDate = dayjs(rangeInfo.start).add(2, 'week');
    setCurrentMonth(middleDate);
    setDateInfo({
      month: middleDate.format('MM'), // Format the month as '09'
      year: middleDate.format('YYYY'), // Format the year as '2024'
    });
  };

  const handleCompanySubmit = (item: { company_id: number; name: string }) => {
    setCompanyDetail(item);
    setIsComapnyModalOpen(false);
    setIsInterviewBlockModalOpen(true);
  };

  const handlePageNo = (newPageNo: number) => {
    setPageNo(newPageNo);
    setPage(newPageNo);
  };

  useEffect(() => {
    setPrevMonth(currentMonth.subtract(1, 'month'));
    setNextMonth(currentMonth.add(1, 'month'));
  }, [currentMonth]);

  useEffect(() => {
    if (selectedCompanyId) {
      setEvents([]);
      setUpcomingInterviews([]);
      setPage(1);
      setPageNo(1);
    }
  }, [selectedCompanyId]);

  // Interview Block - When by time is selected, we should call this  “Interview Block” on the calendar
  // Open Interview - When Open Interview is selected, we should call this “Open Interview” on the calendar
  // Direct Interview - Should say the candidates name - this is the direct invite sent from employer to one candidate

  const allEventsList = [
    ...(Array.isArray(events)
      ? events.map(
          (item: {
            s_status: number;
            title: string;
            event: InterviewBlock;
            start: Date;
            end: Date;
          }) => {
            const color = getEventColor(item.title);
            const isHollow =
              item.event.available_spots === item.event.max_spots
                ? true
                : false;

            return {
              ...item,
              start: dayjs(item?.start).toDate(),
              end: dayjs(item?.end).toDate(),
              backgroundColor: color,
              borderColor: color,
              textColor: white,
              className: isHollow ? 'hollow-event' : '',
              extendedProps: {
                event: item,
                s_status:
                  item.event.available_spots === item.event.max_spots ? 1 : 2,
              },
            };
          },
        )
      : []),
    ...(upcomingInterviews
      ?.filter(
        (item: { slot: any; block: any }) =>
          (item.slot && item.block) || !item.block,
      )
      ?.map(
        (item: {
          block: {
            slots: string | any[];
            dt_start_time: any;
            dt_end_time: any;
          };
          s_status: number;
          slot: {
            slot_start_time: dayjs.Dayjs | Date;
            slot_end_time: dayjs.Dayjs | Date;
          };
          dt_start_time: any;
          dt_end_time: any;
        }) => {
          const title =
            !item.block || item.block?.slots?.length > 0
              ? `${item?.application?.applicant.name}`
              : item.block?.slots?.length > 0
                ? 'Interview Block'
                : 'Open Interviews';
          const color = getEventColor(item?.slot ? 'Interview Block' : title);
          const isHollow = item?.s_status !== 2;

          return {
            start: item.slot
              ? dayjs.utc(item.slot?.slot_start_time).toDate()
              : dayjs
                  .utc(item.block?.dt_start_time ?? item?.dt_start_time)
                  .toDate(),
            end: item.slot
              ? dayjs.utc(item.slot?.slot_end_time).toDate()
              : dayjs
                  .utc(item.block?.dt_end_time ?? item?.dt_end_time)
                  .toDate(),
            title,
            event: item,
            backgroundColor: color,
            borderColor: color,
            textColor: white,
            className: isHollow ? 'hollow-event' : '',
            extendedProps: {
              event: item,
              s_status: item.s_status,
            },
          };
        },
      ) || []),
  ];
  const sortedEvents = allEventsList.sort((a, b) => {
    if (a.title === 'Interview Block' && b.title !== 'Interview Block')
      return -1;
    if (a.title !== 'Interview Block' && b.title === 'Interview Block')
      return 1;
    return 0;
  });
  return (
    <>
      <Helmet>
        <title>Calendar | StaffedUp</title>
      </Helmet>
      <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
        <Stack
          p={3}
          borderRadius="8px"
          bgcolor={white}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h3" fontSize="28px" fontWeight="bold">
            Calendar
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <LocationSearch
              setSelectedCompanyId={setSelectedCompanyId}
              selectedCompanyId={selectedCompanyId}
              handlePageNo={handlePageNo}
              fixWidth
            />
            <Button
              color="success"
              variant="contained"
              onClick={() => setIsComapnyModalOpen(true)}
              startIcon={<AddIcon />}
            >
              Create Interview
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Stack component="section" spacing={1} pl={3} pr={3}>
        <Grid container>
          <Grid
            xs={12}
            md={8}
            item
            p={3}
            sx={{
              '.fc': {
                bgcolor: white,
                p: 3,
                borderRadius: '8px',
              },
            }}
          >
            <style>
              {`
              @media (max-width: 768px) {
                .fc-toolbar {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 8px;
                }
                .fc-toolbar-chunk {
                  width: 100%;
                  text-align: center;
                }
              }

                .hollow-event > .fc-daygrid-event-dot {
                background-color: transparent !important;
                border-radius: 100%;
                width: 5px;
                height: 5px;
                border-width: 3px;
                flex-shrink: 0;
              }
              }
            `}
            </style>
            {(isUpcommingEventsLoading || isLoadingBlocks) && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={64} />
              </Stack>
            )}
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin]}
              initialView="dayGridMonth"
              events={sortedEvents}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              eventClassNames={(arg) => {
                const isHollow = arg.event.extendedProps?.s_status !== 2;
                return isHollow ? ['hollow-event'] : [];
              }}
              eventClick={({ event: { extendedProps } }) => {
                setSelectedEvent(extendedProps.event);
                if (extendedProps.event.max_spots) {
                  setSelectedBlock(extendedProps.event.id);
                } else {
                  setSingleBlockInterview(extendedProps.event);
                }
              }}
              datesSet={handleDateChange}
            />
          </Grid>
          <Grid p={3} item xs={12} md={4}>
            <UpcomingEvents
              upcomingInterviews={upcomingInterviews}
              setSelectedApplication={setSelectedApplication}
              selectedApplication={selectedApplication}
              singleBlockInterview={singleBlockInterview}
              setSingleBlockInterview={setSingleBlockInterview}
            />
          </Grid>
        </Grid>
      </Stack>

      <InterviewBlockModal
        isOpen={isAddInterviewBlockModalOpen}
        handleClose={() => {
          setIsInterviewBlockModalOpen(false);
          setCompanyDetail(null);
        }}
        companyDetail={companyDetail}
      />

      <CompanyDropdownModal
        isOpen={isComapnyModalOpen}
        handleClose={() => {
          setIsComapnyModalOpen(false);
          setCompanyDetail(null);
        }}
        handleSubmit={handleCompanySubmit}
      />

      <EventInfoModal
        handlePageNo={handlePageNo}
        setSelectedApplication={setSelectedApplication}
        selectedApplication={selectedApplication}
        event={selectedEvent}
        blockId={selectedBlock}
        setEvents={setEvents}
        handleClose={() => {
          setSelectedBlock(null);
          setSelectedEvent(null);
        }}
      />
    </>
  );
};
